import {
  BUILTIN_DEFINITION_ID,
  PORTFOLIO_DEFINITION_ID,
} from '@/matic/features/agents/otto/views/hooks/useView';
import {encodeScopeParam, type ScopeItem} from '../scope-utils';

/**
 * Every URL built here gets written into a `.url`/`.webloc` file that lives on
 * a user's desktop **permanently**. There is no server-side redirect in front
 * of them, so a shortcut written today has to keep resolving indefinitely.
 *
 * That makes the Library query grammar (`scope`, `tab`, `template`) a one-way
 * door the moment this ships: retiring or renaming one of those params breaks
 * every shortcut already on every user's machine, silently, with no way to
 * fix it from our side. It has drifted before — `product-observability.ts`
 * still documents a `?ref=` param the Library no longer reads.
 *
 * So: this module is the single place shortcut URLs are constructed, and any
 * param it emits needs a permanent compat shim in `useLibraryRail` rather than
 * a clean removal. Don't inline a URL anywhere else.
 */

/**
 * Marks a navigation as originating from a desktop shortcut. `trackPageViewed`
 * already records the full query string on every route change, so this param
 * is the whole attribution mechanism — `LibraryContainer` reads it, emits
 * `shortcut.opened`, then strips it so it doesn't trail into later navigation.
 */
export const SHORTCUT_SRC_PARAM = 'src';
export const SHORTCUT_SRC_VALUE = 'shortcut';

/** A pin's stored ref shape, which spells the same pair as `ScopeItem`. */
type PinRef = {objectTypeId: string; objectRecordId: string};

export function refsToScopeItems(refs: PinRef[]): ScopeItem[] {
  return refs.map((ref) => ({
    typeId: ref.objectTypeId,
    recordId: ref.objectRecordId,
  }));
}

/**
 * A View instance's scope key back into a URL `scope` param.
 *
 * The two worlds spell a lone Category differently: `normalizeScopeKey`
 * produces `type:{typeId}` (matching the backend's aggregate form), while the
 * URL wants `{typeId}:*`. Feeding the key straight to `parseScopeParam` does
 * not error — it yields `{typeId: 'type', recordId: '<uuid>'}` and produces a
 * quietly broken link. Always route through here.
 */
export function scopeKeyToScopeParam(scopeKey: string): string {
  if (scopeKey.startsWith('type:')) {
    return `${scopeKey.slice('type:'.length)}:*`;
  }

  return scopeKey;
}

/**
 * Whether a Definition is one of the two built-ins (Brief, Portfolio).
 *
 * These are excluded from the Views folder: a Page's own shortcut already
 * lands on its built-in, so emitting one again under `Views/` would put two
 * files pointing at the same page in the same folder.
 */
export function isBuiltinDefinitionId(definitionId: string): boolean {
  return (
    definitionId === BUILTIN_DEFINITION_ID ||
    definitionId === PORTFOLIO_DEFINITION_ID
  );
}

/**
 * A Definition id as the `template` param.
 *
 * Both built-ins are addressed in the URL as the literal `brief` — the param
 * is polymorphic and resolves to Brief on a record scope, Portfolio on a
 * Category scope. Emitting their well-known UUIDs instead makes
 * `useViewsTabController` look for a matching custom Definition, find none,
 * and drop to the empty draft state: a silently wrong page rather than an
 * error.
 */
export function templateParamFor(definitionId: string): string {
  return isBuiltinDefinitionId(definitionId) ? 'brief' : definitionId;
}

/**
 * Selects the Views tab and (optionally) a specific Definition — the `tab`/
 * `template` pair, applied in place onto `params`.
 *
 * This is the one piece of the query grammar `viewShortcutUrl` below and
 * `buildViewShareUrl` (`useViewsTabController.ts`, the "copy link"/export
 * back-link builder) both need to agree on indefinitely — see the module
 * docblock. `definitionId` is `undefined` for either built-in (both callers
 * already collapse a builtin selection to `undefined` before reaching here),
 * which is exactly the case `templateParamFor` would otherwise need the
 * literal built-in id for; `'brief'` covers both, per its own docs.
 *
 * `template` is set only if not already present, which makes this safe for
 * both call shapes: a freshly-built shortcut URL never has one yet (the set
 * always fires), while a share link starts from the current page's params,
 * which may already carry a `template` that must not be clobbered.
 */
export function applyViewSelectionParams(
  params: URLSearchParams,
  definitionId?: string
): void {
  params.set('tab', 'views');
  if (!params.get('template')) {
    params.set(
      'template',
      definitionId ? templateParamFor(definitionId) : 'brief'
    );
  }
}

function libraryUrl(origin: string, params: URLSearchParams): string {
  params.set(SHORTCUT_SRC_PARAM, SHORTCUT_SRC_VALUE);
  return `${origin}/library?${params.toString()}`;
}

/** The Library root — the one shortcut every folder gets. */
export function libraryRootShortcutUrl(origin: string): string {
  return libraryUrl(origin, new URLSearchParams());
}

/** A saved Page, scoped to whatever refs it holds (one, or many). */
export function pageShortcutUrl(origin: string, refs: PinRef[]): string {
  const params = new URLSearchParams();
  params.set('scope', encodeScopeParam(refsToScopeItems(refs)));
  return libraryUrl(origin, params);
}

/** An already-generated View instance on a scope. */
export function viewShortcutUrl(
  origin: string,
  scopeKey: string,
  definitionId: string
): string {
  const params = new URLSearchParams();
  params.set('scope', scopeKeyToScopeParam(scopeKey));
  applyViewSelectionParams(params, definitionId);
  return libraryUrl(origin, params);
}
